/**
 * Mobile menu — hamburger toggle + submenu accordions.
 *
 * Handles:
 *   - Opening / closing the full-screen mobile menu overlay
 *   - Keyboard navigation (Escape to close, focus management)
 *   - In-menu submenu accordion toggles (`.menu-item-has-children`)
 */

/**
 * Hamburger button → full mobile menu open / close.
 *
 * Supports multiple toggle buttons via [data-menu-toggle] (e.g. the
 * standard header button and the fullscreen-template top bar button).
 */
export function initMobileMenu() {
  const toggleBtns = Array.from( document.querySelectorAll('[data-menu-toggle]') );
  const menu = document.querySelector('[data-mobile-menu]');
  const backdrop = document.querySelector('[data-menu-backdrop]');

  if (!toggleBtns.length || !menu) return;

  let isOpen = false;

  function setExpanded(value) {
    isOpen = value;
    toggleBtns.forEach(btn => btn.setAttribute('aria-expanded', String(value)));
  }

  function openMenu() {
    menu.removeAttribute('hidden');
    menu.getBoundingClientRect(); // force reflow so CSS transition plays
    menu.classList.add('is-open');
    backdrop?.classList.add('is-visible');
    setExpanded(true);
    document.body.style.overflow = 'hidden';
    menu.querySelector('a, button')?.focus();
  }

  function closeMenu() {
    menu.classList.remove('is-open');
    backdrop?.classList.remove('is-visible');
    setExpanded(false);
    document.body.style.overflow = '';
    menu.addEventListener('transitionend', () => menu.setAttribute('hidden', ''), { once: true });
  }

  toggleBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      isOpen ? closeMenu() : openMenu();
    });
  });

  document.querySelectorAll('[data-menu-close]').forEach((btn) =>
    btn.addEventListener('click', closeMenu)
  );

  backdrop?.addEventListener('click', closeMenu);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) {
      closeMenu();
      toggleBtns[0]?.focus();
    }
  });

  // Close menu when any internal link is clicked (page navigation)
  menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeMenu));
}

/**
 * Inject toggle buttons for `.menu-item-has-children` in the mobile menu.
 * Creates a row container with the link + a chevron button that opens the sub-menu.
 */
export function initMobileSubmenus() {
  const menuList = document.querySelector('.mobile-menu__list');
  if (!menuList) return;

  menuList.querySelectorAll('.menu-item-has-children').forEach((item) => {
    const link = item.querySelector(':scope > a');
    const submenu = item.querySelector(':scope > .sub-menu');
    if (!link || !submenu) return;

    // Wrap link + toggle in a flex row
    const row = document.createElement('div');
    row.className = 'mobile-menu__item-row';
    link.parentNode.insertBefore(row, link);
    row.appendChild(link);

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mobile-submenu-toggle';
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-label', 'Ouvrir le sous-menu');
    btn.innerHTML =
      '<svg class="mobile-submenu-toggle__icon" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none">' +
      '<path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';
    row.appendChild(btn);

    btn.addEventListener('click', () => {
      const isOpen = submenu.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', String(isOpen));
      btn.setAttribute('aria-label', isOpen ? 'Fermer le sous-menu' : 'Ouvrir le sous-menu');
    });
  });
}
